package ai.flowstorm.studio.ui.design.dialogue.graph

import ai.flowstorm.studio.model.Graph.GeneratedSpeech
import ai.flowstorm.studio.resources.space.DialogueMixinResource
import ai.flowstorm.studio.ui.design.dialogue.GraphEditor
import ai.flowstorm.studio.ui.form.VoiceId
import kotlinx.coroutines.launch
import ai.flowstorm.common.ui.CodeEditor
import ai.flowstorm.common.ui.Form
import ai.flowstorm.common.ui.Icon
import ai.flowstorm.common.ui.UiHelpers
import ai.flowstorm.common.ui.form.ListIdSelect
import ai.flowstorm.core.model.LLMConfig
import ai.flowstorm.studio.ui.form.PersonaId
import io.kvision.core.Overflow
import io.kvision.core.Widget
import io.kvision.core.onEvent
import io.kvision.form.check.CheckBox
import io.kvision.form.select.Select
import io.kvision.form.spinner.Spinner
import io.kvision.form.text.Text
import io.kvision.i18n.I18n.gettext
import io.kvision.i18n.I18n.tr
import io.kvision.panel.simplePanel

class GeneratedSpeechEditor(graphEditor: GraphEditor) : NodeEditor<GeneratedSpeech>(graphEditor), EditableCode {

    init {
        graphEditor.subscribe {
            mainScope.launch {
                mixinSelect.options = UiHelpers.options(DialogueMixinResource.load("Speech", it.language))
            }
        }
    }

    val mixinSelect = ListIdSelect(label = tr("Mixins")).apply {
        mainScope.launch {
            options = UiHelpers.options(DialogueMixinResource.load("Speech", graphEditor.state.language))
        }
        onEvent {
            change = {
                mainScope.launch {
                    codeEditor.set(codeEditor.value, this@apply.getValue() ?: listOf())
                }
            }
        }
    }


    override val codeEditor = CodeEditor("flowstorm", "panel-view-context").apply {
        onEvent {
            change = {
                if (node!!.prompt != self.value) {
                    node?.prompt = self.value
                    graphEditor.commit(gettext("Prompt changed") + " [#${node!!.id}]", "generatedSpeech[${node!!.id}].responses")
                }
            }
        }
    }
    val systemPromptEditor = CodeEditor("flowstorm", "panel-view-context").apply {
        onEvent {
            change = {
                if (node!!.systemPrompt != self.value) {
                    node?.systemPrompt = self.value
                    graphEditor.commit(gettext("System prompt changed") + " [#${node!!.id}]", "generatedSpeech[${node!!.id}].systemPrompt")
                }
            }
        }
    }
    val llmFunctionsEditor = CodeEditor(className = "panel-view-context").apply {
        onEvent {
            change = {
                if (node!!.llmFunctions != self.value) {
                    node?.llmFunctions = self.value
                    graphEditor.commit(gettext("LLM functions changed") + " [#${node!!.id}]", "generatedSpeech[${node!!.id}].llmFunctions")
                }
            }
        }
    }
    val payloadEditor = CodeEditor("yaml", "panel-view-context").apply {
        onEvent {
            change = {
                if (node!!.code != self.value) {
                    node?.code = self.value
                    graphEditor.commit(gettext("Generated Speech payload changed") + " [#${node!!.id}]", "generatedSpeech[${node!!.id}].code")
                }
            }
        }
    }

    override var readOnly: Boolean
        get() = super.readOnly
        set(value) {
            super.readOnly = value
            codeEditor.readOnly = value
            systemPromptEditor.readOnly = value
            llmFunctionsEditor.readOnly = value
            payloadEditor.readOnly = value
            llmConfigForm.disabled = value
            nonLLMConfigForm.disabled = value
        }

    private val persona = PersonaId().apply {
        load(null)
    }

    private val voice = VoiceId().apply {
        load(null)
    }

    override val form = object : NodeForm(GeneratedSpeech.serializer()) {
        init {
            add(
                GeneratedSpeech::label,
                Text(label = tr("Label"))
            )
            add(GeneratedSpeech::name, Text(label = tr("Name")),
                required = true,
                validatorMessage = { gettext("Node name must be unique.") },
                validator = { text ->
                    !graphEditor.graph.nodes.any { it.name == text.value && it != node }
                }
            )
            add(
                GeneratedSpeech::persona_id,
                persona
            )
            add(
                GeneratedSpeech::mixins_id,
                mixinSelect
            )
            add(GeneratedSpeech::background, Text(label = tr("Background")))
            add(GeneratedSpeech::background, Text(label = tr("Style")))
            add(GeneratedSpeech::repeatable, CheckBox(label = tr("Repeatable")))
        }
    }

    val llmConfigForm = object : Form<LLMConfig>(LLMConfig.serializer(), "panel-view-context") {
        init {
            val thisForm = this
            val models= arrayOf("gpt-3.5-turbo", "gpt-4", "gpt-4-1106-preview", "gpt-4o", "claude-3-opus-20240229", "claude-3-sonnet-20240229", "claude-3-haiku-20240307", "claude-3-5-sonnet-20240620")

            add(
                LLMConfig::model,
                Select(label = tr("Model"), options=UiHelpers.options(models))
            )
            add(
                LLMConfig::temperature,
                Spinner(label = tr("Temperature"), min = 0, max = 2, step = 0.01, decimals = 2)
            )
            add(
                LLMConfig::maxTokens,
                Spinner(label = tr("Max tokens"), min = 0, max = 4096, step = 1, decimals = 0)
            )
            add(
                LLMConfig::topP,
                Spinner(label = tr("Top P"),  min = 0, max = 1, step = 0.01, decimals = 2)
            )
            add(
                LLMConfig::frequencyPenalty,
                Spinner(label = tr("Frequency penalty"), min = -2, max = 2, step = 0.01, decimals = 2)
            )
            add(
                LLMConfig::presencePenalty,
                Spinner(label = tr("Presence penalty"), min = -2, max = 2, step = 0.01, decimals = 2)
            )

            this.children!!.forEach {
                if (it is Widget) {
                    it.onEvent { change = { thisForm.save(this@GeneratedSpeechEditor.node!!.llmConfig) } }
                }
            }
        }
    }

    val nonLLMConfigForm = object : Form<GeneratedSpeech>(GeneratedSpeech.serializer(), "panel-view-context") {
        init {
            add(GeneratedSpeech::taskName, Text(label = tr("Task Name")).apply {
                onEvent {
                    change = { if (validate()) { save() } }
                }
            })
            add(GeneratedSpeech::start, Text(label = tr("Start")).apply {
                onEvent {
                    change = { if (validate()) { save() } }
                }
            })
            add(GeneratedSpeech::numTurns, Spinner(label = tr("Number of turns"), min = 0, max = 100, step = 1, decimals = 0).apply {
                onEvent {
                    change = { if (validate()) { save() } }
                }
            })
        }

        fun save() {
            save(node!!)
            graphEditor.diagramPanel.diagram.setNodeProperty(node!!.id, "label", node!!.label)
            graphEditor.commit(
                io.kvision.i18n.gettext("Node property changed") + " [#${node!!.id}]",
                "node[${node!!.id}].comment"
            )
        }
    }

    val configPanel = simplePanel {
        paddingTop = UiHelpers.spacing
        overflow = Overflow.SCROLL
        add(nonLLMConfigForm)
        add(llmConfigForm)
    }

    override val tabPanel = object : NodeTabPanel() {
        init {
            addTab(tr("Properties"), form, Icon.Properties.def)
            addTab(tr("Prompt"), codeEditor, Icon.Text.def)
            addTab(tr("System"), systemPromptEditor, Icon.Text.def)
            addTab(tr("Config"), configPanel, Icon.Properties.def)
            addTab(tr("LLM Functions"), llmFunctionsEditor, Icon.Function.def)
            addTab(tr("Payload"), payloadEditor, Icon.Payload.def)
            activeIndex = 1
        }
    }

    override fun load(node: GeneratedSpeech) {
        super.load(node)
        form.load(node)
        llmConfigForm.setData(node.llmConfig)
        nonLLMConfigForm.setData(node)
        codeEditor.set(node.prompt ?: "", node.mixins_id)
        systemPromptEditor.set(node.systemPrompt ?: "")
        llmFunctionsEditor.set(node.llmFunctions ?:"")
        payloadEditor.set(node.code ?: "")
    }
}